/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IExtNewCheckoutDeliveryOptionsProps } from '../ext-new-checkout-delivery-options.props.autogenerated';
import { IExtNewCheckoutDeliveryOptionsData } from '../ext-new-checkout-delivery-options.data';

/**
 * The header preferred store component.
 */
@observer
export class CentiroPreferredStore extends React.PureComponent<IExtNewCheckoutDeliveryOptionsProps<IExtNewCheckoutDeliveryOptionsData>> {
    private readonly modalCloseButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

    /**
     * Handle click on the store selector.
     * @returns : Promise void.
     */
    public handleClick = () => {
        return this._findPreferredStoreOnClick(this.props);
    };

    public render(): JSX.Element | undefined {
        const { slots, resources } = this.props;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- slots can be undefined.
        if (ArrayExtensions.hasElements(slots?.storeSelector)) {
            const chooseLabel = resources.centiroLocationSelectorLabel;

            return (
                <div className='ms-centiro__preferred-store-container'>
                    <button
                        className='ms-centiro__preferred-store-btn msc-btn'
                        // color='secondary'
                        aria-label={chooseLabel}
                        onClick={this.handleClick}
                        ref={this.modalCloseButtonRef}
                    >
                        {chooseLabel}
                    </button>
                    {slots.storeSelector}
                </div>
            );
        }

        return undefined;
    }

    private async _findPreferredStoreOnClick(
        props: IExtNewCheckoutDeliveryOptionsProps<IExtNewCheckoutDeliveryOptionsData>
    ): Promise<void> {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            modules
        } = props;
        if (!storeSelectorStateManager) {
            return;
        }
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && ArrayExtensions.hasElements(modules.storeSelector)) {
            storeSelectorId = modules.storeSelector[0].id;
        }
        await storeSelectorStateManager
            .openDialog({
                id: storeSelectorId,
                showMapViewLink: true,
                parentElementRef: this.modalCloseButtonRef,
                onLocationSelected: async orgUnitLocation => {
                    await storeSelectorStateManager.closeDialog();
                    return Promise.resolve();
                }
            })
            .catch((error: Error) => {
                if (props.telemetry) {
                    props.telemetry.error(error.message);
                    props.telemetry.debug('Unable to set preferred store');
                }
            });
    }
}

export default CentiroPreferredStore;
